import { Helmet } from 'react-helmet-async';
import { Navigate, useSearchParams } from 'react-router-dom';

import { DailyStats } from './_authenticated.stats/DailyStats';
import green from '../assets/images/rse.png';
import { Dropdown } from '../components/Dropdown';
import { IncomeHistogram } from '../components/IncomeHistogram';
import { SemiDonut } from '../components/SemiDonut';
import { Spinner } from '../components/Spinner';
import { constants } from '../constants';
import { useFetchUser } from '../hooks/useFetchUser';
import { useFetchUserTrees } from '../hooks/useFetchUserTrees';
import { useReceptionists } from '../services/receptionists/useReceptionists';
import { t, tPlural } from '../services/translation/translation';

const renderTarget = (target: string | null) => (!target || target === 'all' ? t('stats.mode-all') : target);

export const StatsRoute = () => {
  const { data: user } = useFetchUser();
  const { data: trees, isLoading } = useFetchUserTrees();
  const { activeReceptionist, receptionists } = useReceptionists();
  const [searchParams, setSearchParams] = useSearchParams();
  const target = searchParams.get('target') ?? 'all';

  const filteredReceptionists = user?.admin ? receptionists : activeReceptionist ? [activeReceptionist] : [];

  const handleChangeTarget = (target: string) => {
    setSearchParams(old => ({ ...old, target }));
  };

  if (!user) {
    return (
      <div className="py-4">
        <Spinner />
      </div>
    );
  }

  if (!user.admin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="bg-background px-4 py-4">
      <Helmet>
        <title>{t('stats.meta-title')}</title>
      </Helmet>
      <div className="flex justify-end">
        <Dropdown<string>
          items={['all', ...filteredReceptionists]}
          selectedItem={target}
          keyExtractor={i => i}
          renderItem={renderTarget}
          renderButton={renderTarget}
          onItemSelected={handleChangeTarget}
          alignment="right"
        />
      </div>
      {constants.isHotels ? (
        <>
          <h1 className="text-primary text-center text-3xl md:text-4xl font-bold mt-2 md:mb-4">{t('stats.by-day')}</h1>
          <DailyStats />
          <h1 className="text-primary text-center text-3xl md:text-4xl font-bold mt-2 md:mb-10">
            {t('stats.earning')}
          </h1>
          <IncomeHistogram receptionist={target !== 'all' ? target : undefined} />
          <hr className="m-10" />
        </>
      ) : null}
      <h1 className="text-primary text-center text-3xl md:text-4xl font-bold mt-10 md:mb-10">{t('stats.title')}</h1>
      <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
        <div className="md:px-6 pt-10 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner className="scale-[3]" />
            </div>
          ) : trees ? (
            <>
              <div className="mx-auto max-w-2xl lg:max-w-lg">
                <p className="mt-2 text-2xl md:text-3xl font-bold text-center">
                  <strong className="text-success mr-1">{500 - trees.currentKms} Km</strong>
                  {t('stats.next-tree')}
                </p>
                {trees.nbTrees > 0 ? (
                  <p className="text-center text-lg mt-1">
                    {t('stats.already')}
                    <strong className="text-success mx-1">{tPlural('stats.trees', { count: trees.nbTrees })}</strong>
                    {t('stats.thanks-to-you')}
                    <br />
                  </p>
                ) : null}
              </div>
              <div className="flex justify-center items-center mt-10 md:mt-0 md:h-full">
                <SemiDonut currentKms={trees.currentKms} fillColor="#00A879"></SemiDonut>
              </div>
            </>
          ) : null}
        </div>
        <div className="px-6 pt-10 lg:col-start-2 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mr-0 lg:max-w-lg">
            <p className="mt-2 text-2xl md:text-3xl font-bold">{t('stats.carbone')}</p>
            <p className="mt-3 md:mt-6 text-lg leading-8">{t('stats.carbone-text')}</p>
          </div>
        </div>
      </div>
      <hr className="m-10" />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="lg:px-32 flex justify-between items-center flex-col md:flex-row-reverse">
          <img className="mx-auto h-48 w-auto md:mr-0" src={green} alt="greenallocab" />
          <div className="mx-auto max-w-2xl lg:mx-0">
            <p className="mt-6 text-xl font-bold leading-7">{t('stats.rse')}</p>
          </div>
        </div>
        <div className="bg-background py-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between">
              <div className="w-full md:w-1/3 mx-auto flex max-w-xs flex-col gap-y-4 bg-success p-8 md:mx-4 mb-5 md:mb-0">
                <p className="text-3xl text-center font-semibold  text-text_light sm:text-5xl">1 {t('stats.tree')}</p>
                <p className="text-base leading-7 text-center text-text_light">{t('stats.planted')}</p>
              </div>
              <div className="w-full md:w-1/3 mx-auto flex max-w-xs flex-col gap-y-4 bg-cta p-8 md:mx-4 mb-5 md:mb-0">
                <p className="text-3xl text-center font-semibold  text-text_light sm:text-5xl">60 %</p>
                <p className="text-base leading-7 text-center text-text_light">{t('stats.green-trips')}</p>
              </div>
              <div className="w-full md:w-1/3 mx-auto flex max-w-xs flex-col gap-y-4 bg-primary p-8 md:mx-4">
                <p className="text-3xl text-center font-semibold  text-text_light sm:text-5xl">35 €</p>
                <p className="text-base leading-7 text-center text-text_light">{t('stats.drivers-income')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
